import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  inject,
  Input,
  ViewChild,
} from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { FileTypeDto, HelpResponseItemDto } from '../../../../../api-main';
import { catchError, map, Observable, of } from 'rxjs';
import { AsyncPipe, NgOptimizedImage } from '@angular/common';
import { SkeletonComponent } from '../../../../shared/components/skeleton/skeleton.component';
import { TranslateService } from '@ngx-translate/core';
import { ApiMainGeneralService } from '../../../../shared/services/api-main-general.service';

@Component({
  selector: 'app-guide-image-view',
  imports: [MatIcon, MatIconButton, AsyncPipe, SkeletonComponent, NgOptimizedImage],
  templateUrl: './guide-image-view.component.html',
  styleUrl: './guide-image-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GuideImageViewComponent {
  private mainGeneralService = inject(ApiMainGeneralService);
  private translateService = inject(TranslateService);

  @Input({ required: true }) items: HelpResponseItemDto[];

  @ViewChild('container') containerElement: ElementRef;

  private _index = 0;

  private cd = inject(ChangeDetectorRef);

  private images: Record<number, string> = {};

  private readonly notFoundSrc = 'assets-common/default/logo_error_icon.svg';

  skeletonHeight = 600;

  get itemsCount() {
    return this.items.length;
  }

  get item(): HelpResponseItemDto {
    return this.items[this.index];
  }

  get index() {
    return this._index;
  }

  set index(index: number) {
    if (index == this._index) return;
    this.setSkeletonSize();
    this._index = index;
    if (this._index < 0) this._index = this.itemsCount - 1;
    if (this._index > this.itemsCount - 1) this._index = 0;
    this.cd.detectChanges();
    this.resetAnimation();
  }

  private setSkeletonSize() {
    const container = this.containerElement.nativeElement;
    const img = container.querySelector('img');
    if (!img) return;
    this.skeletonHeight = img.getBoundingClientRect().height;
  }

  cantMove() {
    return this.itemsCount == 1;
  }

  prev() {
    this.index--;
  }

  next() {
    this.index++;
  }

  getImage(): Observable<string> {
    if (this.images[this.index] != null) return of(this.images[this.index]);
    const imagePath = this.item.image;
    if (!imagePath) return of(null);
    return this.mainGeneralService.getImage(imagePath).pipe(
      // delay(5000),
      map((imgResponse: FileTypeDto) => {
        const isSvg = imagePath.split('.')[1] === 'svg';
        const type = isSvg ? 'svg+xml' : imagePath.split('.')[1];
        const img = `data:image/${type};base64,${imgResponse.file}`;
        this.images[this.index] = img;
        this.resetAnimation();
        return img;
      }),
      catchError(() => {
        this.images[this.index] = this.notFoundSrc;
        this.item.message = this.translateService.instant('ERROR.NO_HELP_IMAGES');

        console.debug('failed to load img');
        return of(null);
      }),
    );
  }

  private resetAnimation() {
    const el = this.containerElement.nativeElement;
    el.style.animation = 'none';
    //this somehow update element
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    el.offsetHeight;
    el.style.animation = '';
  }

  @HostListener('document:keydown', ['$event'])
  protected handleKeyboardEvent(event: KeyboardEvent) {
    if (event.key === 'ArrowLeft') {
      this.prev();
    }
    if (event.key === 'ArrowRight') {
      this.next();
    }
    const number = parseInt(event.key);
    if (!isNaN(number)) this.index = number - 1;
  }

  protected readonly Array = Array;
}
